import { Solutions } from '@services/MarppleShop/entities/pool/public/solutions.ts';
import differenceInDays from 'date-fns/differenceInDays/index.js';

export const SOLUTION_THREAD_EXPIRATION = 14;

export const SOLUTION_TYPE = {
  COPYRIGHT: 'copyright',
  FAKE_PRODUCT: 'fake-product',
  INTERNAL_REGULATION: 'internal-regulation',
} as const;

export const SOLUTION_STATUS = {
  ING: 'ing',
  ING_2: 'ing2',
  CONFIRM: 'confirm',
  CLOSE: 'close',
} as const;

export const SOLUTION_USER_TYPE = {
  INDIVIDUAL: 'person',
  GROUP: 'group',
} as const;

export const SOLUTION_THREAD_STATUS = {
  IN_PROGRESS: 'IN_PROGRESS',
  RE_IN_PROGRESS: 'RE_IN_PROGRESS',
  END: 'END',
  ANSWER_EXPIRATION: 'ANSWER_EXPIRATION',
} as const;

export class SolutionHelper {
  /**
   * 신고하기 답변 여부
   */
  static isAnswer(solution: Solutions) {
    return !!solution.is_answer;
  }

  /**
   * 신고 대상 상품의 판매 중지 여부
   */
  static isProductSuspension(solution: Solutions) {
    return (
      solution.type !== SOLUTION_TYPE.INTERNAL_REGULATION &&
      (solution.status === SOLUTION_STATUS.CLOSE || solution.status === SOLUTION_STATUS.ING)
    );
  }

  /**
   * 신고 대상 스토어의 운영 중지 여부
   */
  static isStoreSuspension(solution: Solutions) {
    return solution.type === SOLUTION_TYPE.INTERNAL_REGULATION && solution.status === SOLUTION_STATUS.CLOSE;
  }

  /**
   * 스토어 신고
   */
  static isStoreSolution(solution: Solutions) {
    return !solution.product_id && !!solution.store_id && solution.type === SOLUTION_TYPE.INTERNAL_REGULATION;
  }

  /**
   * 상품 신고
   */
  static isProductSolution(solution: Solutions) {
    return !!solution.product_id;
  }

  /**
   * 크리에이터 디자인 신고
   */
  static isDesignSolution(solution: Solutions) {
    return !!solution.mcon_theme_id;
  }

  /**
   * 신고하기 스레드 종료 여부
   *
   * 1. 답변하고 14일 지남
   * 2. 답변하지 않고 14일 지남
   * 3. 신고자가 쓰레드 종료 처리함
   */
  static isThreadExpiration(solution: Solutions) {
    const thread_status = SolutionHelper.thread_status(solution);
    return !!solution.is_close || thread_status === SOLUTION_THREAD_STATUS.ANSWER_EXPIRATION;
  }

  /**
   * 신고하기 스레드 상태
   */
  static thread_status(solution: Solutions) {
    // 답변하지 않고 14일 지남, 재소기간 종료
    if (
      !solution.is_answer &&
      solution.last_request_at &&
      differenceInDays(new Date(), new Date(solution.last_request_at)) > SOLUTION_THREAD_EXPIRATION
    )
      return SOLUTION_THREAD_STATUS.ANSWER_EXPIRATION;

    // 답변하고 14일 지남, 스레드 종료
    if (
      solution.is_answer &&
      solution.last_answer_at &&
      differenceInDays(new Date(), new Date(solution.last_answer_at)) > SOLUTION_THREAD_EXPIRATION
    )
      return SOLUTION_THREAD_STATUS.END;

    // 스레드 종료
    if (solution.is_close) return SOLUTION_THREAD_STATUS.END;

    // 신고자 답변 요청 기간
    if (
      solution.is_answer &&
      solution.last_answer_at &&
      differenceInDays(new Date(), new Date(solution.last_answer_at)) <= SOLUTION_THREAD_EXPIRATION
    )
      return SOLUTION_THREAD_STATUS.RE_IN_PROGRESS;

    // 답변 진행 중
    if (differenceInDays(new Date(), new Date(solution.created_at)) <= SOLUTION_THREAD_EXPIRATION)
      return SOLUTION_THREAD_STATUS.IN_PROGRESS;

    throw new Error(`SolutionHelper Not Exist thread_status`);
  }
}
